import { Input, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { useState } from 'react';
import { ACCEPTED_FILE_EXTENSION } from 'components/NudosComponents/BordUploaderButton/BordUploaderButton.types';
import { CloseNewCountryIcon, DeleteIcon, UploadIcon } from 'assets/images/countryList';
import { TAny } from 'types/blog';
import { ICountryDetail, INewCountryForm } from 'types/countries';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import NewCountryCurrencySelect from '../newCountryCurrencySelect/NewCountryCurrencySelect';
import { getFileExtension } from '../../countriesTab.utils';

import './addCountryForm.scss';

interface IAddCountryForm {
  className?: string;
  onSubmit?: (data: INewCountryForm) => void;
  availableList?: TAny;
  registerList?: ICountryDetail[];
  onClose?: () => void;
}

/**
 * Formulario para registrar un país nuevo en el catálogo.
 */
const AddCountryForm = ({ className = '', onSubmit, availableList, registerList, onClose }: IAddCountryForm) => {
  const [countryFormData, setCountryFormData] = useState<INewCountryForm>();
  const [isoCodeError, setIsoCodeError] = useState<string>();

  /** Valida y asigna el archivo de bandera del país. */
  const handleFlagUpload = (file: RcFile) => {
    const extension = getFileExtension(file.name);

    if (extension === ACCEPTED_FILE_EXTENSION.PNG) {
      setCountryFormData(previousData => ({ ...previousData, flag: file }));
      return;
    }

    displayErrorNotification({ customJSXMessage: <>solo puedes subir archivos PNG</> });
  };

  /** Verifica que el ISO code exista en el catálogo disponible. */
  const verifyIsoCode = (code?: string) => {
    if (!code) return false;

    const countryName = availableList[code];

    if (countryName) {
      setCountryFormData(previousData => ({ ...previousData, name: countryName }));
      return true;
    }

    return false;
  };

  /** Verifica que el país no esté ya registrado. */
  const verifyCountryIsNotRegistered = (code?: string) => {
    if (!code) return true;

    if (registerList?.length) {
      const existingCountry = registerList.find(element => element.code === code);
      return !existingCountry;
    }

    return true;
  };

  /** Envía el formulario si cumple las validaciones de negocio. */
  const handleSubmitCountryForm = () => {
    const isValidIsoCode = verifyIsoCode(countryFormData?.ISOCode || '');
    const isCountryAvailable = verifyCountryIsNotRegistered(countryFormData?.ISOCode || '');

    setIsoCodeError(!isValidIsoCode ? 'Este ISO CODE no existe ' : !isCountryAvailable ? 'País ya registrado' : '');

    if (onSubmit && isValidIsoCode && isCountryAvailable && countryFormData) {
      onSubmit(countryFormData);
    }
  };

  /** Actualiza el ISO code y el nombre sugerido del país. */
  const handleIsoCodeChange = (code: string) => {
    if (!code) {
      setCountryFormData(previousData => ({ ...previousData, name: '', ISOCode: code }));
      return;
    }

    const countryName = availableList[code];

    if (countryName) {
      setCountryFormData(previousData => ({ ...previousData, name: countryName, ISOCode: code }));
      return;
    }

    setCountryFormData(previousData => ({ ...previousData, name: '', ISOCode: code }));
  };

  /** Abre la referencia externa de códigos ISO. */
  const openIsoCodesReference = () => {
    window.open('https://www.datosmundial.com/codigos-de-pais.php', '_blank');
  };

  const isSubmitDisabled =
    !countryFormData?.ISOCode ||
    !countryFormData?.flag ||
    !countryFormData?.currencyType ||
    countryFormData?.currencyExchangeRate == null ||
    countryFormData?.currencyExchangeRate <= 0 ||
    !countryFormData?.documentName ||
    !!isoCodeError;

  return (
    <div className="addCountryForm">
      <div className={`addCountryFormContainer ${className || ''}`}>
        <div className="addCountryFormField mr-16">
          <div
            className={`addCountryFormLabel ${isoCodeError ? 'errorLabel' : ''} ${
              countryFormData?.ISOCode ? 'filledLabel' : ''
            }`}
          >
            ISO CODE
          </div>
          <Input
            onChange={e => {
              setIsoCodeError(undefined);
              handleIsoCodeChange(e.target.value.toLowerCase());
            }}
            className={`countryInputStyle ${isoCodeError ? 'errorInput' : ''} ${
              countryFormData?.ISOCode ? 'filledInput' : ''
            }`}
            placeholder="Ej. US"
          />
          {isoCodeError ? (
            <div className="errorContainer">
              <div className="errorText">{isoCodeError}</div>
            </div>
          ) : (
            <div className="floatLinkBox">
              <div className="blueText" onClick={openIsoCodesReference}>
                https://www.datosmundial.com/codigos-de-pais.php
              </div>
            </div>
          )}
        </div>

        <div className="addCountryFormField mr-16">
          <div className="addCountryFormLabel">Nombre del país </div>
          <div className="containerNameCountry">
            <div className="countryName">{countryFormData?.name}</div>
          </div>
        </div>

        <div className="boxFileContainer mr-16">
          <div className="addCountryFormLabel">Bandera</div>
          <div className="uploadItem">
            {countryFormData?.flag ? (
              <div
                className="myFile"
                onClick={() => {
                  setCountryFormData(previousData => ({ ...previousData, flag: undefined }));
                }}
              >
                <DeleteIcon />
              </div>
            ) : (
              <Upload
                accept={ACCEPTED_FILE_EXTENSION.PNG}
                customRequest={uploadEvent => {
                  handleFlagUpload(uploadEvent.file as RcFile);
                  uploadEvent.onSuccess?.('ok');
                }}
                showUploadList={false}
              >
                <div className="noFile">
                  <UploadIcon />
                </div>
              </Upload>
            )}
          </div>
        </div>

        <div className="addCountryFormField mr-16">
          <div className={`addCountryFormLabel ${countryFormData?.currencyType ? 'filledLabel' : ''}`}>
            Tipo de moneda
          </div>
          <NewCountryCurrencySelect
            selectedCurrencyCode={countryFormData?.currencyType}
            onCurrencyChange={currency => {
              setCountryFormData(previousData => ({
                ...previousData,
                currencyType: currency.code,
                currencyExchangeRate: currency.exchangeRate
              }));
            }}
          />
        </div>

        <div className="addCountryFormField mr-48">
          <div className={`addCountryFormLabel ${countryFormData?.documentName ? 'filledLabel' : ''}`}>
            Nombre del docuemento fiscal
          </div>
          <Input
            className={`countryInputStyle ${countryFormData?.documentName ? 'filledInput' : ''}`}
            placeholder="Ej. RFC"
            onChange={e => {
              setCountryFormData(previousData => ({ ...previousData, documentName: e.target.value }));
            }}
          />
        </div>

        <div className="addCountryFormSubmitWrapper">
          <button
            type="button"
            onClick={handleSubmitCountryForm}
            disabled={isSubmitDisabled}
            className="addCountryFormSubmitButton"
          >
            Agregar país
          </button>
        </div>

        <div className="addCountryFormCloseWrapper">
          <CloseNewCountryIcon onClick={onClose} className="addCountryFormCloseIcon" />
        </div>
      </div>
    </div>
  );
};

export default AddCountryForm;
